define('presentation/common/snackbar/viewModels/onboardingSnackbarViewModel',['i18next',
    'businessServices/authentication/accountPlanInfo',
    'presentation/welcome/common/onboardingNavigationManager'
], function() {
    return function() {
        const self = this;

        const _i18n = require('i18next');
        const _accountPlanInfo = require('businessServices/authentication/accountPlanInfo');
        const OnboardingNavigationManagerConstructor = require('presentation/welcome/common/onboardingNavigationManager');

        let _onboardingNavigationManager = null;

        const SERVICE_TYPE = {
            LIVE_RECEPTIONIST: 'liveReceptionist',
            WEB_CHAT: 'webChat',
            OUTBOUND_CALLING: 'outboundCalling'
        };

        const SERVICE_DISPLAY_NAME = {
            [SERVICE_TYPE.LIVE_RECEPTIONIST]: 'call answering',
            [SERVICE_TYPE.WEB_CHAT]: 'web chat',
            [SERVICE_TYPE.OUTBOUND_CALLING]: 'outbound calling'
        };

        const SERVICE_PRODUCT_NAME_KEY = {
            [SERVICE_TYPE.LIVE_RECEPTIONIST]: 'onboardingSnackbar:serviceLiveCallAnswering',
            [SERVICE_TYPE.WEB_CHAT]: 'onboardingSnackbar:serviceLiveWebChat',
            [SERVICE_TYPE.OUTBOUND_CALLING]: 'onboardingSnackbar:serviceOutboundCalls'
        };

        const SERVICE_SCHEDULED_CHECK = {
            [SERVICE_TYPE.LIVE_RECEPTIONIST]: () => _accountPlanInfo.isLiveReceptionistOnboardingScheduled(),
            [SERVICE_TYPE.WEB_CHAT]: () => _accountPlanInfo.isWebChatOnboardingScheduled(),
            [SERVICE_TYPE.OUTBOUND_CALLING]: () => _accountPlanInfo.isOutboundCallingOnboardingScheduled()
        };

        const CONTENT_KEY = {
            ONBOARDING_CALL_BOOKED: 'onboardingCallBooked',
            ONBOARDING_CALL_BOOKED_NO_ACTION: 'onboardingCallBookedNoAction',
            SINGLE_SERVICE_NOT_STARTED: 'singleServiceNotStarted',
            MULTIPLE_SERVICES_NOT_STARTED: 'multipleServicesNotStarted',
            LIVE_RECEPTIONIST_WIZARD_DONE_ONBOARDING_CALL_OUTSTANDING: 'liveReceptionistWizardDoneOnboardingCallOutstanding'
        };

        self.isDismissed = ko.observable(false);

        self.allOutstandingServices = ko.pureComputed(() => {
            const services = [];
            if (_accountPlanInfo.hasLiveReceptionistPackage() && !_accountPlanInfo.isLiveReceptionistOnboardingComplete()) {
                services.push(SERVICE_TYPE.LIVE_RECEPTIONIST);
            }
            if (_accountPlanInfo.hasWebChatPackage() && !_accountPlanInfo.isWebChatOnboardingComplete()) {
                services.push(SERVICE_TYPE.WEB_CHAT);
            }
            if (_accountPlanInfo.hasOutboundCallingPackage() && !_accountPlanInfo.isOutboundCallingOnboardingComplete()) {
                services.push(SERVICE_TYPE.OUTBOUND_CALLING);
            }
            return services;
        });

        self.isVisible = ko.pureComputed(() => {
            return self.isDismissed() === false && self.allOutstandingServices().length > 0;
        });

        const _hasLiveReceptionistOutstanding = ko.pureComputed(() => {
            return self.allOutstandingServices().indexOf(SERVICE_TYPE.LIVE_RECEPTIONIST) !== -1;
        });

        const _onboardingCallServicesOutstanding = ko.pureComputed(() => {
            return self.allOutstandingServices().filter((service) => service !== SERVICE_TYPE.LIVE_RECEPTIONIST);
        });

        const _isLiveReceptionistOnboardingDone = ko.pureComputed(() => {
            return _accountPlanInfo.hasLiveReceptionistPackage() && _accountPlanInfo.isLiveReceptionistOnboardingComplete();
        });

        const _isOnboardingCallBooked = ko.pureComputed(() => {
            const outstandingServices = self.allOutstandingServices();

            return outstandingServices.length > 0 &&
                outstandingServices.every((service) => SERVICE_SCHEDULED_CHECK[service]());
        });

        const _joinServiceNames = (services) => {
            const names = services.map((service) => SERVICE_DISPLAY_NAME[service]);
            if (names.length <= 1) {
                return names.join('');
            }
            if (names.length === 2) {
                return `${names[0]} and ${names[1]}`;
            }
            return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
        };

        const _contentKey = ko.pureComputed(() => {
            const hasLiveReceptionist = _hasLiveReceptionistOutstanding();
            const hasOnboardingCallService = _onboardingCallServicesOutstanding().length > 0;

            if (_isOnboardingCallBooked()) {
                // The call covers the specialist services either way; the only thing left to offer
                // is message taking in the meantime, and only where live receptionist is the
                // outstanding piece.
                return hasLiveReceptionist ?
                    CONTENT_KEY.ONBOARDING_CALL_BOOKED :
                    CONTENT_KEY.ONBOARDING_CALL_BOOKED_NO_ACTION;
            }

            if (hasLiveReceptionist && hasOnboardingCallService) {
                return CONTENT_KEY.MULTIPLE_SERVICES_NOT_STARTED;
            }

            if (hasLiveReceptionist) {
                // Stays "not started" even after the wizard has been opened: the saved record already
                // holds their progress, so the snackbar always offers the same way back in.
                return CONTENT_KEY.SINGLE_SERVICE_NOT_STARTED;
            }

            if (hasOnboardingCallService) {
                if (_isLiveReceptionistOnboardingDone()) {
                    return CONTENT_KEY.LIVE_RECEPTIONIST_WIZARD_DONE_ONBOARDING_CALL_OUTSTANDING;
                }

                return _onboardingCallServicesOutstanding().length > 1 ?
                    CONTENT_KEY.MULTIPLE_SERVICES_NOT_STARTED :
                    CONTENT_KEY.SINGLE_SERVICE_NOT_STARTED;
            }

            return null;
        });

        self.messageKey = ko.pureComputed(() => {
            return _contentKey() === null ? null : `onboardingSnackbar:${_contentKey()}Message`;
        });

        self.messageOptions = ko.pureComputed(() => {
            const outstandingServices = self.allOutstandingServices();

            return {
                services: _joinServiceNames(outstandingServices),
                serviceName: outstandingServices.length === 1 ? _i18n.t(SERVICE_PRODUCT_NAME_KEY[outstandingServices[0]]) : ''
            };
        });

        self.showPrimaryAction = ko.pureComputed(() => {
            return _hasLiveReceptionistOutstanding();
        });

        self.showScheduleCallAction = ko.pureComputed(() => {
            // A booked call needs no scheduling button, whether or not anything else is left to do.
            return _isOnboardingCallBooked() === false && self.showPrimaryAction() === false;
        });

        self.isDismissible = ko.pureComputed(() => {
            // Only offered against a booked call: every other scenario leaves the customer with an
            // outstanding action and nothing else pointing them back to it.
            return _isOnboardingCallBooked();
        });

        self.primaryActionKey = ko.pureComputed(() => {
            return _isOnboardingCallBooked() ?
                'onboardingSnackbar:configureMessageTakingService' :
                'onboardingSnackbar:getStarted';
        });

        self.onPrimaryActionClicked = () => {
            _onboardingNavigationManager.captureEntryPathname();

            if (_isOnboardingCallBooked()) {
                // Scheduling is already settled, so this is the message-taking upsell rather than
                // another route into booking a call.
                _onboardingNavigationManager.navigateToFlowStartPage();
                return;
            }

            if (_onboardingCallServicesOutstanding().length > 0) {
                // live receptionist + an onboarding call service: a call is needed either way, so
                // "Get started" schedules rather than entering the wizard directly.
                _onboardingNavigationManager.navigateToSchedulingPage();
                return;
            }

            // live receptionist service only: always let the customer choose self-service vs. a
            // call, including on a return visit after "I'll finish this later".
            _onboardingNavigationManager.navigateToWelcomePage();
        };

        self.onScheduleCallClicked = () => {
            _onboardingNavigationManager.captureEntryPathname();
            _onboardingNavigationManager.navigateToSchedulingPage();
        };

        self.onDismiss = () => {
            self.isDismissed(true);
        };

        self.activate = () => {
            _onboardingNavigationManager = new OnboardingNavigationManagerConstructor();
        };
    };
});

